import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { initialsOf } from '@/components/ui';
import { useRailBadges } from './useRailBadges';
import { MobileHeader } from './MobileHeader';
import { MobileTabBar } from './MobileTabBar';
import { MoreSheet } from './MoreSheet';
import { pathnameToTabId, mobileMoreItems } from './mobileNav';
import { ContactSupportModal, useContactSupport } from './ContactSupportModal';

interface MobileShellProps {
  history: { push: (path: string) => void; location: { pathname: string } };
  onLogout: () => void;
  children: React.ReactNode;
}

export const MobileShell = ({
  history,
  onLogout,
  children,
}: MobileShellProps) => {
  const [moreOpen, setMoreOpen] = useState(false);

  // Same guarded derivation as AppShell: loggedUser.data may be {} on first render.
  const data = useSelector((s: any) => s.loggedUser?.data) || {};
  const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  const initials = initialsOf(fullName || data.emailAddress || '?');
  const isExternal = data.userType === 'External';
  // Role-gated More items (e.g. Admin Reports) read capability flags from /me — BE decides,
  // FE renders. Default-deny: {} until /me resolves.
  const permissions = useSelector((s: any) => s.me?.permissions) || {};

  const { messages, notifications } = useRailBadges();
  const counts: Record<string, number> = { messages, notifications };

  const activeTabId = pathnameToTabId(history.location.pathname || '/');
  const moreItems = mobileMoreItems(isExternal, permissions);
  const moreActive = moreItems.some((m) => m.id === activeTabId);

  const navigate = (path: string) => {
    history.push(path);
    setMoreOpen(false);
  };
  const { isContactSupportOpen, openContactSupport, closeContactSupport } =
    useContactSupport();

  return (
    <div className="tw-flex tw-flex-col tw-h-screen tw-w-full tw-overflow-hidden tw-relative">
      <MobileHeader
        initials={initials}
        name={fullName || data.emailAddress || ''}
        // Matches the desktop rail avatar (-> /profile). The More sheet is reached
        // via the bottom "More" tab, so the avatar doesn't double as a sheet trigger.
        onAvatarClick={() => navigate('/profile')}
      />
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-bg-white">
        {children}
      </div>
      <MobileTabBar
        activeTabId={activeTabId}
        moreOpen={moreOpen}
        moreActive={moreActive}
        counts={counts}
        onNavigate={navigate}
        onToggleMore={() => setMoreOpen((v) => !v)}
      />
      <MoreSheet
        open={moreOpen}
        items={moreItems}
        activeTabId={activeTabId}
        onNavigate={navigate}
        onClose={() => setMoreOpen(false)}
        onLogout={onLogout}
        onContactSupport={openContactSupport}
      />
      <ContactSupportModal
        isOpen={isContactSupportOpen}
        onClose={closeContactSupport}
      />
    </div>
  );
};
